import React, {FC} from "react"
import styled, {css} from "styled-components"

import Flex from "@irati/ui/src/Flex"
import {Body, H1, H2} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import {useTheme} from "@irati/ui/src/global/useTheme"
import maxWidthMedia, {minWidthMedia} from "@irati/ui/src/global/media"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

interface Props {
  title?: string
  subTitle?: string
  hasAlert?: boolean
  imagePosition: "left" | "right"
  body: string
  ctaText: string
}

const ProductInfoSection: FC<Props> = ({title, subTitle, imagePosition, body, ctaText, hasAlert}) => {
  const theme = useTheme()
  const {formatMessage} = useIntl()

  return (
    <Wrapper>
      <Container>
        {title && (
          <H1 color={theme.colors.primary2}>
            <FormattedMessage id={title} />
          </H1>
        )}
        {hasAlert && (
          <Alert noMargin nonDismissable type={AlertTypes.warning} title="annualLeavePurchase.landing.section.general.alert.text" />
        )}
        <Content>
          {imagePosition === "left" && (
            <Flex flex="1">
              <Image
                src={formatMessage({id: "annualLeavePurchase.landing.section.general.imageLeft.src"})}
                alt="ALP-left-image"
                imagePosition={imagePosition}
              />
            </Flex>
          )}
          <TextCard>
            {subTitle && (
              <H2 color={theme.colors.primary2}>
                <FormattedMessage id={subTitle} />
              </H2>
            )}

            <Body>
              <FormattedMessage id={body} />
            </Body>

            <Flex>
              <Button primary mobileFull onClick={() => {}}>
                <FormattedMessage id={ctaText} />
              </Button>
            </Flex>
          </TextCard>
          {imagePosition === "right" && (
            <Flex flex="1">
              <Image
                src={formatMessage({id: "annualLeavePurchase.landing.section.general.imageRight.src"})}
                alt="ALP-right-image"
                imagePosition={imagePosition}
              />
            </Flex>
          )}
        </Content>
      </Container>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  background: ${({theme}) => theme.colors.greys.white};
`

const Container = styled(Flex)`
  ${({theme}) => css`
    flex-direction: column;
    padding: ${theme.spacing.six} ${theme.spacing.two};
    gap: ${theme.spacing.oneAndHalf};
    width: 100%;

    ${minWidthMedia.tablet`
      width: 80%;
      gap: ${theme.spacing.three};
    `}
  `}
`

const Content = styled(Flex)`
  ${({theme}) => css`
    border-radius: 12px;
    flex-direction: column;
    border: none;
    box-shadow: none;
    gap: 12px;
    ${minWidthMedia.tablet`
      flex-direction: row;
      border: 1px solid ${theme.colors.greys.grey20};
      box-shadow: ${theme.shadows.low};
      gap: 0;
    `}
  `}
`

const TextCard = styled(Flex)`
  ${({theme}) => css`
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: ${theme.spacing.three};
    padding: 0;
    ${minWidthMedia.tablet`
      padding: ${theme.spacing.three} ${theme.spacing.six};
    `}
  `}
`

const Image = styled.img<{imagePosition: string}>`
  ${({imagePosition, theme}) => css`
    width: 100%;
    height: auto;
    object-fit: cover;
    ${imagePosition === "left"
      ? `border-top-left-radius: ${theme.border.radius.extraLarge};
         border-bottom-left-radius: ${theme.border.radius.extraLarge};`
      : `border-top-right-radius: ${theme.border.radius.extraLarge};
         border-bottom-right-radius: ${theme.border.radius.extraLarge};`}

    ${maxWidthMedia.tablet`
      border-radius: ${theme.border.radius.extraLarge};
    `}
  `}
`

export default ProductInfoSection
